import React, { useState, useEffect } from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  EmailField,
  UrlField,
  ArrayField,
  SingleFieldList,
  ChipField,
  Loading,
  TopToolbar,
  EditButton,
  DeleteButton,
  ListButton
} from 'react-admin';
import { apiClient } from '../../framework/api/api.client';
import { RJSFSchema } from '@rjsf/utils';

interface DynamicEntityShowV2Props {
  entityMetadata?: any;
  schemaKey?: string;
  entityName?: string;
}

/**
 * Dynamic Entity Show - renders all fields from schema as read-only
 */
export const DynamicEntityShowV2: React.FC<DynamicEntityShowV2Props> = ({
  entityMetadata,
  schemaKey,
  entityName
}) => {
  const [schema, setSchema] = useState<any>(null);
  const [loading, setLoading] = useState(!!schemaKey);

  useEffect(() => {
    if (!schemaKey) {
      setLoading(false);
      return;
    }

    const fetchSchema = async () => {
      try {
        setLoading(true);
        const fetchedSchema = await apiClient.get(`/schema/key/${schemaKey}`);
        setSchema(fetchedSchema);
      } catch (err) {
        console.error('❌ Failed to fetch schema for show:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchema();
  }, [schemaKey]);

  const renderField = (fieldName: string, fieldDef: any) => {
    const label = formatLabel(fieldName);
    const type = fieldDef.type;
    const format = fieldDef.format;

    // Arrays
    if (type === 'array') {
      return (
        <ArrayField key={fieldName} source={fieldName} label={label}>
          <SingleFieldList>
            <ChipField source="$" />
          </SingleFieldList>
        </ArrayField>
      );
    }

    // Booleans
    if (type === 'boolean') {
      return <BooleanField key={fieldName} source={fieldName} label={label} />;
    }

    // Numbers
    if (type === 'number' || type === 'integer') {
      return <NumberField key={fieldName} source={fieldName} label={label} />;
    }

    // Dates
    if (format === 'date' || format === 'date-time') {
      return <DateField key={fieldName} source={fieldName} label={label} showTime={format === 'date-time'} />;
    }

    // Emails
    if (format === 'email' || fieldName.toLowerCase().includes('email')) {
      return <EmailField key={fieldName} source={fieldName} label={label} />;
    }

    // URLs
    if (format === 'uri' || format === 'url' || fieldName.toLowerCase().includes('url') || fieldName.toLowerCase().includes('website')) {
      return <UrlField key={fieldName} source={fieldName} label={label} />;
    }

    // Default to TextField
    return <TextField key={fieldName} source={fieldName} label={label} />;
  };

  if (loading) {
    return (
      <Show title={entityName || 'Details'}>
        <Loading />
      </Show>
    );
  }

  // Get fields from schema
  const jsonSchema = schema?.jsonSchema ? JSON.parse(schema.jsonSchema) : null;
  const properties = jsonSchema?.properties || {};
  
  // Filter out internal fields
  const visibleFields = Object.entries(properties).filter(([fieldName]) => 
    !['_id', '__v', 'context', 'derivedBy', 'derivedFrom'].includes(fieldName)
  );

  const ShowActions = () => (
    <TopToolbar>
      <EditButton />
      <DeleteButton />
      <ListButton />
    </TopToolbar>
  );

  return (
    <Show title={entityName || schema?.name || 'Details'} actions={<ShowActions />}>
      <SimpleShowLayout>
        {visibleFields.map(([fieldName, fieldDef]) => renderField(fieldName, fieldDef))}
      </SimpleShowLayout>
    </Show>
  );
};

function formatLabel(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

